import React, { ReactNode } from "react";
import { Link } from "polyrouter";
import { TenantType } from "generated/graphql";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { TableBulkItemRow } from "collections/Table";
import { Loader } from "elements";
import { useAuth0 } from "@auth0/auth0-react";
import { AccountForTable } from "../Accounts";
import { TABLES } from "store/userMeta";
import VisibilitySensor from "react-visibility-sensor";
import { PayloadAction } from "typesafe-actions";
import { HasPermissionFunction } from "rbac";
import { MemberRoleNames } from "roles";
import { Maybe } from "generated/graphql.features";

type WithTenant = {
  count?: Maybe<number>;
  fetching: boolean;
};

const AccountCell: React.FC<WithTenant & { "data-testid": string }> = ({
  fetching,
  count,
  ...rest
}) => {
  return (
    <Loader loading={fetching} className="inline text-left">
      <p data-testid={rest["data-testid"]}>
        {typeof count === "number" ? count : "N/A"}
      </p>
    </Loader>
  );
};

const Devices: React.FC<WithTenant> = props => (
  <AccountCell data-testid="Devices" {...props} />
);

const Sites: React.FC<WithTenant> = props => (
  <AccountCell data-testid="Sites" {...props} />
);

const Rooms: React.FC<WithTenant> = props => (
  <AccountCell data-testid="Rooms" {...props} />
);

const Users: React.FC<WithTenant> = props => (
  <AccountCell data-testid="Users" {...props} />
);

const Role: React.FC<{ role: LocalKeys; fetching: boolean }> = ({
  fetching,
  role
}) => {
  const { t } = useTranslation();
  return (
    <Loader loading={fetching} className="inline text-left">
      <p data-testid="user-role">
        {MemberRoleNames.includes(role) ? t(role) : t("NONE")}
      </p>
    </Loader>
  );
};

const Type: React.FC<{ tenantType: TenantType; fetching: boolean }> = ({
  fetching,
  tenantType
}) => {
  const { t } = useTranslation();
  return (
    <Loader loading={fetching} className="inline text-left">
      <p data-testid="tenant-type">{t(tenantType)}</p>
    </Loader>
  );
};

export const AccountRow: React.FC<{
  account: AccountForTable;
  shownColumns: Array<string>;
  index: number;
  listLength: number;
  incrementFrom: () => PayloadAction<"IncrementFrom", number>;
  count: number;
  checked: boolean;
  onCheck: () => void;
  fetching: boolean;
  hasPermission: HasPermissionFunction;
  hasNextPage: boolean;
}> = ({
  account,
  shownColumns,
  index,
  listLength,
  incrementFrom,
  checked,
  onCheck,
  fetching,
  hasPermission,
  hasNextPage
}) => {
  const isItemToTriggerQuery = !hasNextPage ? false : index === listLength - 1;
  const { user } = useAuth0();
  const { COLUMNS } = TABLES.ACCOUNTS;
  const onChange = (isVisible: boolean) => {
    if (isVisible) incrementFrom();
  };

  // need an array of permission names for what's behind there - update_tenant grant:role
  const userCanViewDetails =
    hasPermission("UPDATE_TENANT", account.id) ||
    hasPermission("grant:role", account.id) ||
    hasPermission("register:device", account.id);

  const getTableContent = (column: string) => {
    let testId: string | undefined = undefined;
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        testId = "account-title";
        content = (
          <>
            {/** If the user's role on the account is admin or IT Admin, they are allowed to see account details. If not, just render the name. */}
            {userCanViewDetails && (
              <Link to={`/admin/accounts/details/${account.id}`}>
                {account.name ?? account.id}
              </Link>
            )}
            {!userCanViewDetails && account.name}
          </>
        );
        break;
      case COLUMNS.SITES:
        const siteCount = account.sites.count;
        content = <Sites fetching={fetching} count={siteCount} />;
        break;
      case COLUMNS.ROOMS:
        content = (
          <Rooms fetching={fetching} count={account.rooms?.count ?? 0} />
        );
        break;
      case COLUMNS.DEVICES:
        content = <Devices fetching={fetching} count={account.deviceCount} />;
        break;
      case COLUMNS.MEMBERS:
        content = <Users fetching={fetching} count={account.memberCount} />;
        break;
      case COLUMNS.ROLE:
        content = user && (
          <Role role={account.role ?? ""} fetching={fetching} />
        );
        break;
      case COLUMNS.TENANT_TYPE:
        content = <Type tenantType={account.type} fetching={fetching} />;
    }

    return (
      <td data-testid={testId} key={column}>
        {content}
      </td>
    );
  };

  const Item = (
    <TableBulkItemRow
      key={account.id}
      checked={checked}
      onCheck={onCheck}
      replaceCheckWith={
        <input
          type="checkbox"
          onChange={onCheck}
          checked={checked}
          disabled={!hasPermission("UPDATE_TENANT", account.id)}
        />
      }
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );

  return isItemToTriggerQuery ? (
    <VisibilitySensor
      onChange={onChange}
      containment={document.getElementById("accounts-table")}
      partialVisibility
    >
      {Item}
    </VisibilitySensor>
  ) : (
    Item
  );
};
